import React, { useEffect, useRef, useState } from 'react'
import { useMapsLibrary } from '@vis.gl/react-google-maps'
import TextField from '@mui/material/TextField'

interface PlaceAutocompleteProps {
  required?: boolean
  initialAddress?: string
  onPlaceSelect: (place: google.maps.places.PlaceResult | null) => void
}

const LegacyGoogleMapsAutocomplete = ({ required, initialAddress, onPlaceSelect }: PlaceAutocompleteProps) => {
  const [placeAutocomplete, setPlaceAutocomplete] =
    useState<google.maps.places.Autocomplete | null>(null)
  const [value, setValue] = useState(initialAddress || '')
  const [hasValidPlace, setHasValidPlace] = useState(!!initialAddress)
  const inputRef = useRef<HTMLInputElement>(null)
  const isUserTyping = useRef(false)
  const places = useMapsLibrary('places')

  useEffect(() => {
    if (isUserTyping.current) return
    setValue(initialAddress || '')
    setHasValidPlace(!!initialAddress)
  }, [initialAddress])

  useEffect(() => {
    if (!places || !inputRef.current) return

    const options = {
      fields: ['geometry', 'name', 'formatted_address']
    }

    setPlaceAutocomplete(new places.Autocomplete(inputRef.current, options))
  }, [places])

  useEffect(() => {
    if (!placeAutocomplete) return

    const listener = placeAutocomplete.addListener('place_changed', () => {
      const place = placeAutocomplete.getPlace()
      isUserTyping.current = false

      if (place.formatted_address) {
        setValue(place.formatted_address)
        setHasValidPlace(true)
        onPlaceSelect(place)
      } else {
        setHasValidPlace(false)
        onPlaceSelect(null)
      }
    })

    return () => {
      google.maps.event.removeListener(listener)
    }
  }, [onPlaceSelect, placeAutocomplete])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    isUserTyping.current = true
    setValue(newValue)
    setHasValidPlace(false)
    onPlaceSelect(null)
  }

  const handleBlur = () => {
    isUserTyping.current = false
  }

  return (
    <TextField
      required={required}
      size='small'
      placeholder='Pesquise um endereço'
      value={value}
      onChange={handleChange}
      onBlur={handleBlur}
      onKeyDown={(e) => e.stopPropagation()}
      slotProps={{
        htmlInput: { ref: inputRef, required }
      }}
      error={required && !hasValidPlace}
    />
  )
}

export default LegacyGoogleMapsAutocomplete